import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

// New-name poll (Jack, 2026-10-19). Shown once on the lobby to each signed-in
// player who hasn't voted and has finished at least one game. "Ask me later" hides it until Jangle is next opened
// (sessionStorage). Votes are saved per account (backend/name_poll.py).
const LATER_KEY = "jangle:name-poll-later";

function askedLater() {
  try {
    return sessionStorage.getItem(LATER_KEY) === "1";
  } catch {
    return false;
  }
}

// The ranking itself: 5 numbered slots and a chip per name. Used by the lobby
// pop-up and by the shareable /poll page.
export function NamePollForm({ poll, onVoted, secondary = null }) {
  const [pick, setPick] = useState([]);
  const [sending, setSending] = useState(false);

  const toggle = (name) => {
    setPick((p) => (p.includes(name) ? p.filter((n) => n !== name) : p.length < poll.picks ? [...p, name] : p));
  };

  const submit = async () => {
    setSending(true);
    try {
      await api.post("/name-poll", { ranking: pick });
      toast.success("Thanks for voting!");
      onVoted(pick);
    } catch (err) {
      if (err?.response?.status === 409) onVoted(null);
      toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't send, please try again");
    } finally {
      setSending(false);
    }
  };

  const slots = Array.from({ length: poll.picks }, (_, i) => pick[i]);
  return (
    <>
      <div className="grid gap-1.5">
        {slots.map((name, i) => (
          <div
            key={i}
            className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl border text-sm ${
              name ? "border-border bg-background" : "border-dashed border-border"
            }`}
          >
            <span
              className={`w-6 h-6 flex-none rounded-full flex items-center justify-center text-xs font-bold border-2 border-brand ${
                name ? "bg-brand text-brand-foreground" : "text-brand"
              }`}
            >
              {i + 1}
            </span>
            {name ? (
              <span className="font-semibold">{name}</span>
            ) : (
              <span className="text-muted-foreground">{i === 0 ? "Your favourite" : "Tap a name"}</span>
            )}
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="name-poll-names">
        {poll.names.map((name) => {
          const k = pick.indexOf(name);
          return (
            <button
              key={name}
              type="button"
              onClick={() => toggle(name)}
              className={`px-3 py-1.5 rounded-full border text-sm flex items-center gap-1.5 transition-colors ${
                k >= 0
                  ? "border-brand text-brand font-semibold bg-brand/10"
                  : "border-border hover:bg-surface-hover"
              }`}
            >
              {k >= 0 && <span className="text-[11px] font-bold">{k + 1}</span>}
              {name}
            </button>
          );
        })}
      </div>

      <div className={`flex items-center gap-2 pt-1 ${secondary ? "justify-between" : "justify-end"}`}>
        {secondary}
        <Button
          onClick={submit}
          disabled={sending || pick.length < poll.picks}
          className="bg-brand hover:bg-brand-hover text-brand-foreground rounded-full px-6"
          data-testid="name-poll-submit"
        >
          {sending ? "Sending…" : `Submit my top ${poll.picks}`}
        </Button>
      </div>
    </>
  );
}

export const POLL_INTRO = (picks) =>
  `We need your help to pick a new name for the game. Tap your top ${picks} names from the list, with 1 being your favourite. Tap a name again to take it off.`;

export default function NamePoll({ blocked = false }) {
  const [poll, setPoll] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (askedLater()) return;
    let live = true;
    api.get("/name-poll/me").then(
      ({ data }) => live && !data.voted && data.played && setPoll(data),
      () => {}
    );
    return () => { live = false; };
  }, []);

  // Wait until no other lobby pop-up is showing.
  useEffect(() => {
    if (poll && !blocked) setOpen(true);
  }, [poll, blocked]);

  const later = () => {
    try { sessionStorage.setItem(LATER_KEY, "1"); } catch { /* ignore */ }
    setOpen(false);
    setPoll(null);
  };
  const done = () => {
    setOpen(false);
    setPoll(null);
  };

  if (!poll) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && later()}>
      <DialogContent
        className="sm:max-w-lg w-[calc(100vw-2rem)] max-h-[90dvh] overflow-y-auto bg-surface border-border rounded-2xl"
        data-testid="name-poll"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Help us pick a new name!</DialogTitle>
          <DialogDescription className="text-muted-foreground">{POLL_INTRO(poll.picks)}</DialogDescription>
        </DialogHeader>
        <NamePollForm
          poll={poll}
          onVoted={done}
          secondary={
            <Button variant="ghost" onClick={later} data-testid="name-poll-later">
              Ask me later
            </Button>
          }
        />
      </DialogContent>
    </Dialog>
  );
}
